<template>
    <div class="animated">
        <form @submit.prevent="onSubmit" role="form">
            <h2 class="title is-5 has-text-white">{{ $t('Two-factor authentication') }}</h2>

            <b-field
                :label="$t('Authentication code')"
                :type="form.errors.has('token2fa') ? 'is-danger' : null"
                :message="form.errors.get('token2fa')"
            >
                <b-input
                    v-model="form.token2fa"
                    :placeholder="$t('Authentication code')"
                    icon="key"
                    name="token2fa"
                    required
                    autofocus
                    size="is-medium"
                />
            </b-field>

            <div class="field">
                <p class="control">
                    <button type="submit" class="button is-primary is-medium is-fullwidth">{{ $t('Verify') }}</button>
                </p>
            </div>

            <b-icon icon="cellphone" size="is-size-4" class="is-pulled-left"></b-icon>
            <div style="margin-left: 25px">
                {{ hint }}
                <hr style="margin: 0.75rem 0" />

                <!-- Only shown when there is genuinely another way in, so the
                     offer is never a dead end. -->
                <strong>{{ $t("Don't have your phone?") }}</strong>
                <br />
                <a v-for="method in otherMethods" :key="method.id" @click="useMethod(method)" class="is-block">
                    {{ $t('Use {method}', { method: method.label }) }}
                </a>
                <!-- The field accepts one directly: at this point the user has
                     nothing else left, so an extra step would be cruel. -->
                <span class="is-block is-size-7 has-text-grey">
                    {{ $t('You can also enter one of your recovery codes above.') }}
                </span>

                <router-link :to="{ name: 'login' }">
                    {{ $t('Back to login') }}
                </router-link>
            </div>
        </form>
    </div>
</template>

<script>
import { useToast } from '@/composables/useToast';

let fields = {};

export default {
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        // Init fields
        fields = {
            token2fa: ''
        };

        return {
            form: new Form(fields),
            to: '/app',
            methods2fa: [],
            activeMethod: null
        };
    },

    computed: {
        // Everything except the one already challenged — offering the current
        // method as an alternative to itself reads as broken.
        otherMethods() {
            return this.methods2fa.filter(m => m.id !== this.activeMethod?.id);
        },

        hint() {
            if (this.activeMethod && this.activeMethod.type !== 'totp') {
                return i18n.t('We sent a code to {method}. Enter it below.', { method: this.activeMethod.label });
            }

            return i18n.t(
                'Open the two-factor authentication app on your device to view your authentication code and verify your identity.'
            );
        }
    },

    mounted() {
        document.title = i18n.t('Two-factor authentication') + ` | ${AppTitle}`;

        this.loadMethods();

        // The /2fa navigation re-renders app.blade, so `Session::pull('url.intended')`
        // has already been consumed and `window.intended` is '/' here. Fall back to (and
        // keep persisting) the localStorage copy set on the login page so the post-2FA
        // redirect still targets the originally requested page.
        const intended = window.intended && window.intended !== '/' ? window.intended : null;
        if (intended) {
            localStorage.setItem('redirectAfterLogin', intended);
        }

        const redirect = localStorage.getItem('redirectAfterLogin');
        if (redirect) {
            this.to = redirect;
        }
    },

    methods: {
        loadMethods() {
            axios
                .get('/2fa/challenge-methods')
                .then(response => {
                    this.methods2fa = response.data.methods || [];
                    this.activeMethod = this.methods2fa[0] || null;
                })
                .catch(() => {
                    // The screen still works with the app's own code.
                });
        },

        // Switch to another factor and, when it delivers, ask it for a code.
        useMethod(method) {
            axios
                .post('/2fa/challenge', { method_id: method.id })
                .then(response => {
                    this.activeMethod = method;
                    if (response.data.sent) {
                        this.toast.success(i18n.t('The code was sent successfully!'));
                    }
                })
                .catch(error => {
                    new AxiosError(error.response).manage();
                });
        },

        onSubmit() {
            this.form
                .post('/2fa/verify')
                .then(response => {
                    if (response.data.bridge_redirect) {
                        // Cross-domain bridge — redirect to custom domain with exchange code
                        window.location.href = response.data.bridge_redirect;
                    } else {
                        localStorage.removeItem('redirectAfterLogin');
                        // The server knows where this challenge came from; the
                        // locally guessed target is only a fallback.
                        window.location = response.data.redirect_to || this.to;
                    }
                })
                .catch(error => {});
        }
    }
};
</script>
